/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import { node, number } from 'prop-types';
import style from './ScreenAdjustableOverlay.module.css';

const ScreenAdjustableOverlay = ({
    minHeight, defaultHeight, topContainer, bottomContainer, dividerContainer,
}) => {
    useStyles(style);

    const DEFAULT_HEIGHT = defaultHeight;
    const MIN_HEIGHT = minHeight;
    const [height, setHeight] = useState(DEFAULT_HEIGHT);

    useEffect(() => { setHeight(DEFAULT_HEIGHT); }, [DEFAULT_HEIGHT]);

    const startY = useRef();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        const handleMouseMove = (e) => {
            setIsDragging(true);
            const clientY = e.clientY || e.touches?.[0]?.clientY;
            if (startY.current !== null) {
                setHeight((prevHeight) => {
                    const prevProps = prevHeight === 'auto' ? DEFAULT_HEIGHT : prevHeight;
                    const newHeight = prevProps + (clientY - startY.current);
                    if (MIN_HEIGHT > newHeight) {
                        setIsExpanded(!isExpanded);
                    }

                    if (newHeight >= MIN_HEIGHT && newHeight <= DEFAULT_HEIGHT) {
                        startY.current = clientY;
                        setIsExpanded(true);
                        return newHeight;
                    }
                    if (newHeight < DEFAULT_HEIGHT) {
                        setIsExpanded(true);
                    } else {
                        setIsExpanded(false);
                    }
                    return prevHeight;
                });
            }
        };

        const handleMouseUp = () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
            document.body.style.overflow = 'auto';
            document.body.style.userSelect = 'auto';
            setTimeout(() => setIsDragging(false), 0);
        };

        const handleMouseDown = (e) => {
            const clientY = e.clientY || e.touches?.[0]?.clientY;
            startY.current = clientY;
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleMouseMove);
            window.addEventListener('touchend', handleMouseUp);
            document.body.style.overflow = 'hidden';
            document.body.style.userSelect = 'none';
        };

        const handleClick = () => {
            if (!isDragging) {
                if (isExpanded) {
                    setHeight(DEFAULT_HEIGHT); // collapse to  max height
                } else {
                    setHeight(MIN_HEIGHT); // expand to min height
                }
                setIsExpanded(!isExpanded);
            }
        };

        const divider = document.querySelector('.divider');
        divider.addEventListener('mousedown', handleMouseDown);
        divider.addEventListener('touchstart', handleMouseDown);
        divider.addEventListener('click', handleClick);

        return () => {
            divider.removeEventListener('mousedown', handleMouseDown);
            divider.removeEventListener('touchstart', handleMouseDown);
            divider.removeEventListener('click', handleClick);
        };
    }, [isExpanded, isDragging, DEFAULT_HEIGHT]);
    return (
        <div className={`${style.container} container`}>
            <div
                className={`${style.topDiv} ${!isDragging ? style.expandable : ''} ${isExpanded ? style.expanded : ''}  topDiv`}

                style={{
                    '--topDiv-height': height === 'auto' ? 'auto' : `${height}px`,
                }}
            >
                {topContainer}
            </div>
            <div className={`${style.divider} divider`}>{dividerContainer}</div>
            <div className={`${style.bottomDiv} bottomDiv`}>{bottomContainer}</div>
        </div>
    );
};

ScreenAdjustableOverlay.propTypes = {
    bottomContainer: node,
    topContainer: node,
    dividerContainer: node,
    minHeight: number,
    defaultHeight: number,
};

ScreenAdjustableOverlay.defaultProps = {
    bottomContainer: <></>,
    topContainer: <></>,
    dividerContainer: <></>,
    minHeight: 100,
    defaultHeight: 300,
};
export default ScreenAdjustableOverlay;
